import { useMemo } from 'react';

const ORDER = 9;
const REGION_ORDER = 3;
const CELL_COUNT = ORDER * ORDER;
const SQRT3_OVER_2 = Math.sqrt(3) / 2;
const BOARD_SIDE = 900;
const CELL_SIDE = BOARD_SIDE / ORDER;
const BOARD_HEIGHT = BOARD_SIDE * SQRT3_OVER_2;
const VIEWBOX_PADDING = 24;
const BOARD_CENTER_X = VIEWBOX_PADDING + BOARD_SIDE / 2;
const BOARD_TOP = VIEWBOX_PADDING;
const VIEWBOX_WIDTH = BOARD_SIDE + VIEWBOX_PADDING * 2;
const VIEWBOX_HEIGHT = BOARD_HEIGHT + VIEWBOX_PADDING * 2 + 4;
const MINOR_STROKE_WIDTH = BOARD_SIDE * 0.0031;
const MAJOR_STROKE_WIDTH = BOARD_SIDE * 0.0105;
const VALUE_FONT_SIZE = CELL_SIDE * 0.54;
const CANDIDATE_FONT_SIZE = 20;
const FONT_FAMILY = 'Helvetica, Arial, sans-serif';

const DEFAULT_PALETTE = {
  background: '#ffffff',
  board: '#ffffff',
  cell: '#ffffff',
  outer: '#4C7FAE',
  inner: '#D8EAF4',
  overlap: '#82A8C4',
  stroke: '#1d1916',
  givenText: '#1d1916',
  answerText: '#56616b',
  candidateText: '#53585e',
  shadow: '#d8d2c8',
  sourceOutline: '#23864B',
  peerOutline: '#E23A2E',
  assignedOutline: '#0891b2',
  eliminatedOutline: '#d97706',
  patternOutline: '#23864B',
  overlapOutline: '#2563eb',
  assignedHighlight: '#a5f3fc',
  eliminatedHighlight: '#fde68a',
  patternHighlight: '#bbf7d0',
  overlapHighlight: '#bfdbfe',
};

const roundedPoint = (x, y) => ({
  x: Number(x.toFixed(5)),
  y: Number(y.toFixed(5)),
});

const pointKey = (point) => `${point.x.toFixed(5)},${point.y.toFixed(5)}`;

const edgeKey = (pointA, pointB) =>
  [pointKey(pointA), pointKey(pointB)].sort().join('|');

const polygonPoints = (points) =>
  points.map((point) => `${point.x.toFixed(5)},${point.y.toFixed(5)}`).join(' ');

const midpoint = (pointA, pointB) => ({
  x: (pointA.x + pointB.x) / 2,
  y: (pointA.y + pointB.y) / 2,
});

const polygonCentroid = (points) => ({
  x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
  y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
});

const triangleRowPoints = (apexX, apexY, side, row) => {
  const rowHeight = side * SQRT3_OVER_2;
  const topY = apexY + row * rowHeight;
  const bottomY = apexY + (row + 1) * rowHeight;
  const topPoints = Array.from({ length: row + 1 }, (_, index) =>
    roundedPoint(apexX - (row * side) / 2 + index * side, topY)
  );
  const bottomPoints = Array.from({ length: row + 2 }, (_, index) =>
    roundedPoint(apexX - ((row + 1) * side) / 2 + index * side, bottomY)
  );

  return { topPoints, bottomPoints };
};

const trianglePoints = (apexX, apexY, side, row, position) => {
  const { topPoints, bottomPoints } = triangleRowPoints(apexX, apexY, side, row);
  const pointIndex = Math.floor(position / 2);

  if (position % 2 === 0) {
    return [topPoints[pointIndex], bottomPoints[pointIndex], bottomPoints[pointIndex + 1]];
  }

  return [topPoints[pointIndex], topPoints[pointIndex + 1], bottomPoints[pointIndex + 1]];
};

const pointInTriangle = (point, triangle) => {
  const sign = (pointA, pointB, pointC) =>
    (pointA.x - pointC.x) * (pointB.y - pointC.y) -
    (pointB.x - pointC.x) * (pointA.y - pointC.y);
  const first = sign(point, triangle[0], triangle[1]);
  const second = sign(point, triangle[1], triangle[2]);
  const third = sign(point, triangle[2], triangle[0]);
  const hasNegative = first < 0 || second < 0 || third < 0;
  const hasPositive = first > 0 || second > 0 || third > 0;

  return !(hasNegative && hasPositive);
};

const offsetPointToward = (point, target, distance) => {
  const vectorX = target.x - point.x;
  const vectorY = target.y - point.y;
  const length = Math.hypot(vectorX, vectorY);

  if (!length) {
    return point;
  }

  return {
    x: point.x + (vectorX / length) * distance,
    y: point.y + (vectorY / length) * distance,
  };
};

const buildGeometry = () => {
  const regionPolygons = [];
  const regionSide = CELL_SIDE * REGION_ORDER;
  const macroOrder = ORDER / REGION_ORDER;

  for (let row = 0; row < macroOrder; row += 1) {
    for (let position = 0; position <= 2 * row; position += 1) {
      regionPolygons.push({
        id: `${row}:${position}`,
        points: trianglePoints(BOARD_CENTER_X, BOARD_TOP, regionSide, row, position),
      });
    }
  }

  const cells = [];
  for (let row = 0; row < ORDER; row += 1) {
    for (let position = 0; position <= 2 * row; position += 1) {
      const points = trianglePoints(BOARD_CENTER_X, BOARD_TOP, CELL_SIDE, row, position);
      const centroid = polygonCentroid(points);
      const region = regionPolygons.find((candidate) =>
        pointInTriangle(centroid, candidate.points)
      );

      cells.push({
        index: row * row + position,
        row,
        position,
        points,
        centroid,
        pointsUp: position % 2 === 0,
        regionId: region?.id,
        edgeKeys: [],
      });
    }
  }

  const edges = new Map();
  cells.forEach((cell) => {
    [[0, 1], [1, 2], [2, 0]].forEach(([startIndex, endIndex]) => {
      const start = cell.points[startIndex];
      const end = cell.points[endIndex];
      const key = edgeKey(start, end);
      cell.edgeKeys.push(key);

      if (!edges.has(key)) {
        edges.set(key, {
          key,
          start,
          end,
          count: 0,
          regions: new Set(),
        });
      }

      const edge = edges.get(key);
      edge.count += 1;
      edge.regions.add(cell.regionId);
    });
  });

  edges.forEach((edge) => {
    edge.isMajor = edge.count === 1 || edge.regions.size > 1;
  });

  const outerCells = new Set(
    cells
      .filter((cell) => cell.edgeKeys.some((key) => edges.get(key).count === 1))
      .map((cell) => cell.index)
  );

  const outerTriangle = [
    roundedPoint(BOARD_CENTER_X, BOARD_TOP),
    roundedPoint(BOARD_CENTER_X - BOARD_SIDE / 2, BOARD_TOP + BOARD_HEIGHT),
    roundedPoint(BOARD_CENTER_X + BOARD_SIDE / 2, BOARD_TOP + BOARD_HEIGHT),
  ];
  const innerTriangle = [
    midpoint(outerTriangle[0], outerTriangle[1]),
    midpoint(outerTriangle[0], outerTriangle[2]),
    midpoint(outerTriangle[1], outerTriangle[2]),
  ];
  const innerCentroid = polygonCentroid(innerTriangle);
  const innerCells = new Set();

  [[0, 1], [1, 2], [2, 0]].forEach(([startIndex, endIndex]) => {
    const start = innerTriangle[startIndex];
    const end = innerTriangle[endIndex];

    for (let index = 0; index < ORDER; index += 1) {
      const fraction = (index + 0.5) / ORDER;
      const sample = {
        x: start.x + (end.x - start.x) * fraction,
        y: start.y + (end.y - start.y) * fraction,
      };
      const insetSample = offsetPointToward(sample, innerCentroid, CELL_SIDE * 0.08);
      const cell = cells.find((candidate) => pointInTriangle(insetSample, candidate.points));
      if (cell) {
        innerCells.add(cell.index);
      }
    }
  });

  return {
    cells,
    edges,
    outerCells,
    innerCells,
    outerTriangle,
  };
};

const GEOMETRY = buildGeometry();

const normalizedCellIndexes = (cells) => {
  if (!Array.isArray(cells)) {
    return [];
  }

  return [
    ...new Set(
      cells
        .map((cell) => Number(cell))
        .filter((cell) => Number.isInteger(cell) && cell >= 0 && cell < CELL_COUNT)
    ),
  ];
};

const connectedCellComponents = (cellIndexes) => {
  const selected = new Set(cellIndexes);
  const edgeOwners = new Map();

  cellIndexes.forEach((cellIndex) => {
    GEOMETRY.cells[cellIndex].edgeKeys.forEach((key) => {
      edgeOwners.set(key, [...(edgeOwners.get(key) || []), cellIndex]);
    });
  });

  const neighbors = new Map(cellIndexes.map((cellIndex) => [cellIndex, new Set()]));
  edgeOwners.forEach((owners) => {
    if (owners.length === 2) {
      neighbors.get(owners[0]).add(owners[1]);
      neighbors.get(owners[1]).add(owners[0]);
    }
  });

  const components = [];
  while (selected.size) {
    const first = selected.values().next().value;
    const pending = [first];
    const component = [];
    selected.delete(first);

    while (pending.length) {
      const current = pending.pop();
      component.push(current);
      neighbors.get(current).forEach((neighbor) => {
        if (selected.delete(neighbor)) {
          pending.push(neighbor);
        }
      });
    }

    components.push(component);
  }

  return components;
};

const boundaryPathForComponent = (cellIndexes) => {
  const edgeCounts = new Map();

  cellIndexes.forEach((cellIndex) => {
    GEOMETRY.cells[cellIndex].edgeKeys.forEach((key) => {
      edgeCounts.set(key, (edgeCounts.get(key) || 0) + 1);
    });
  });

  const adjacency = new Map();
  const points = new Map();
  edgeCounts.forEach((count, key) => {
    if (count !== 1) {
      return;
    }

    const edge = GEOMETRY.edges.get(key);
    const startKey = pointKey(edge.start);
    const endKey = pointKey(edge.end);
    points.set(startKey, edge.start);
    points.set(endKey, edge.end);
    adjacency.set(startKey, [...(adjacency.get(startKey) || []), endKey]);
    adjacency.set(endKey, [...(adjacency.get(endKey) || []), startKey]);
  });

  if (!adjacency.size) {
    return '';
  }

  const startKey = [...adjacency.keys()].sort((left, right) => {
    const leftPoint = points.get(left);
    const rightPoint = points.get(right);
    return leftPoint.y - rightPoint.y || leftPoint.x - rightPoint.x;
  })[0];
  const orderedKeys = [startKey];
  let previousKey = null;
  let currentKey = startKey;
  let traversedEdges = 0;

  while (traversedEdges <= adjacency.size) {
    const candidates = adjacency.get(currentKey) || [];
    let nextKey = candidates[0];
    if (previousKey !== null && nextKey === previousKey && candidates.length > 1) {
      nextKey = candidates[1];
    }
    if (!nextKey || nextKey === startKey) {
      break;
    }

    orderedKeys.push(nextKey);
    previousKey = currentKey;
    currentKey = nextKey;
    traversedEdges += 1;
  }

  const orderedPoints = orderedKeys.map((key) => points.get(key));
  return [
    `M ${orderedPoints[0].x.toFixed(5)} ${orderedPoints[0].y.toFixed(5)}`,
    ...orderedPoints
      .slice(1)
      .map((point) => `L ${point.x.toFixed(5)} ${point.y.toFixed(5)}`),
    'Z',
  ].join(' ');
};

const unitOutlinePaths = (unit) => {
  const cellIndexes = normalizedCellIndexes(unit?.cells);
  if (!cellIndexes.length) {
    return [];
  }

  return connectedCellComponents(cellIndexes)
    .map(boundaryPathForComponent)
    .filter(Boolean);
};

const normalizedDigits = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  return [
    ...new Set(
      values
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 1 && value <= 9)
    ),
  ].sort((left, right) => left - right);
};

const candidateRows = (digits) => {
  if (digits.length <= 3) {
    return [digits];
  }
  if (digits.length <= 5) {
    return [digits.slice(0, 2), digits.slice(2)];
  }

  const rows = [];
  for (let index = 0; index < digits.length; index += 3) {
    rows.push(digits.slice(index, index + 3));
  }
  return rows;
};

const candidatePositions = (cell, digits) => {
  const rows = candidateRows(digits);
  const lineHeight = CANDIDATE_FONT_SIZE * 0.92;
  const startY = cell.centroid.y - ((rows.length - 1) * lineHeight) / 2;
  const positions = [];

  rows.forEach((row, rowIndex) => {
    let offsets;
    if (row.length === 1) {
      offsets = [0];
    } else if (row.length === 2) {
      offsets = [-CANDIDATE_FONT_SIZE * 0.48, CANDIDATE_FONT_SIZE * 0.48];
    } else {
      offsets = row.map(
        (_, index) =>
          (index - (row.length - 1) / 2) * CANDIDATE_FONT_SIZE * 0.72
      );
    }

    const baselineY =
      startY + rowIndex * lineHeight + CANDIDATE_FONT_SIZE * 0.34;
    row.forEach((digit, index) => {
      positions.push({ digit, x: cell.centroid.x + offsets[index], y: baselineY });
    });
  });

  return positions;
};

const normalizedCandidateEntry = (cell, entry) => {
  const rawValues = Array.isArray(entry) ? entry : entry?.values;
  const eliminatedValues = normalizedDigits([
    ...(Array.isArray(entry?.eliminatedValues) ? entry.eliminatedValues : []),
    ...(Array.isArray(entry?.removedValues) ? entry.removedValues : []),
  ]);
  return {
    cell,
    values: normalizedDigits([...(Array.isArray(rawValues) ? rawValues : []), ...eliminatedValues]),
    retainedValues: new Set(normalizedDigits(entry?.retainedValues)),
    eliminatedValues: new Set(eliminatedValues),
    circledValues: new Set(normalizedDigits(entry?.circledValues)),
  };
};

const candidateDisplayMap = (candidates, candidateDisplays) => {
  const result = new Map();

  if (Array.isArray(candidates)) {
    candidates.forEach((entry, index) => {
      if (Array.isArray(entry)) {
        result.set(index, normalizedCandidateEntry(index, entry));
      } else if (entry && Number.isInteger(Number(entry.cell))) {
        const cell = Number(entry.cell);
        if (cell >= 0 && cell < CELL_COUNT) {
          result.set(cell, normalizedCandidateEntry(cell, entry));
        }
      }
    });
  }

  if (Array.isArray(candidateDisplays)) {
    candidateDisplays.forEach((entry) => {
      const cell = Number(entry?.cell);
      if (Number.isInteger(cell) && cell >= 0 && cell < CELL_COUNT) {
        result.set(cell, normalizedCandidateEntry(cell, entry));
      }
    });
  }

  return result;
};

const resolvePalette = (palette) => ({
  ...DEFAULT_PALETTE,
  ...palette,
  board: palette?.board ?? palette?.boardFill ?? DEFAULT_PALETTE.board,
  cell: palette?.cell ?? palette?.defaultCell ?? DEFAULT_PALETTE.cell,
  outer: palette?.outer ?? palette?.outerEdgeFill ?? DEFAULT_PALETTE.outer,
  inner: palette?.inner ?? palette?.innerTriangleFill ?? DEFAULT_PALETTE.inner,
  overlap: palette?.overlap ?? palette?.overlapFill ?? DEFAULT_PALETTE.overlap,
  answerText:
    palette?.answerText ?? palette?.walkthroughAnswerText ?? DEFAULT_PALETTE.answerText,
});

const legacyValuePosition = (cell) => {
  let nudgeX = 0;
  let nudgeY = 0;

  cell.edgeKeys.forEach((key) => {
    const edge = GEOMETRY.edges.get(key);
    if (!edge.isMajor) {
      return;
    }

    const edgeMidpoint = midpoint(edge.start, edge.end);
    const vectorX = cell.centroid.x - edgeMidpoint.x;
    const vectorY = cell.centroid.y - edgeMidpoint.y;
    const length = Math.hypot(vectorX, vectorY);
    if (length) {
      nudgeX += vectorX / length;
      nudgeY += vectorY / length;
    }
  });

  const nudgeScale = Math.min(
    CELL_SIDE * 0.08,
    Math.max(MAJOR_STROKE_WIDTH - MINOR_STROKE_WIDTH, 0) * 0.45
  );

  return {
    x: cell.centroid.x + nudgeX * nudgeScale,
    y: cell.centroid.y + nudgeY * nudgeScale + VALUE_FONT_SIZE * 0.34,
  };
};

const cellBaseFill = (cellIndex, palette) => {
  const isOuter = GEOMETRY.outerCells.has(cellIndex);
  const isInner = GEOMETRY.innerCells.has(cellIndex);

  if (isOuter && isInner) {
    return palette.overlap;
  }
  if (isOuter) {
    return palette.outer;
  }
  if (isInner) {
    return palette.inner;
  }
  return palette.cell;
};

/**
 * Display-only renderer for the canonical 81-cell Tridoku board.
 *
 * Unit objects may carry any public metadata (such as type, kind, id, or index),
 * but their `cells` array is the sole source used to draw an outline. This keeps
 * the renderer independent of puzzle corpora and avoids visible unit numbering.
 */
export default function TridokuBoard({
  values = [],
  givens = [],
  candidates = [],
  candidateDisplays = [],
  assignedCells = [],
  eliminatedCells = [],
  patternCells = [],
  overlapCells = [],
  sourceUnit = null,
  peerUnit = null,
  palette = {},
  ariaLabel = 'Tridoku puzzle',
  className = 'h-auto w-full',
}) {
  const colors = useMemo(() => resolvePalette(palette), [palette]);
  const givenSet = useMemo(() => new Set(normalizedCellIndexes(givens)), [givens]);
  const assignedSet = useMemo(
    () => new Set(normalizedCellIndexes(assignedCells)),
    [assignedCells]
  );
  const eliminatedSet = useMemo(
    () => new Set(normalizedCellIndexes(eliminatedCells)),
    [eliminatedCells]
  );
  const patternSet = useMemo(
    () => new Set(normalizedCellIndexes(patternCells)),
    [patternCells]
  );
  const overlapSet = useMemo(
    () => new Set(normalizedCellIndexes(overlapCells)),
    [overlapCells]
  );
  const displays = useMemo(
    () => candidateDisplayMap(candidates, candidateDisplays),
    [candidates, candidateDisplays]
  );
  const sourcePaths = useMemo(() => unitOutlinePaths(sourceUnit), [sourceUnit]);
  const peerPaths = useMemo(() => unitOutlinePaths(peerUnit), [peerUnit]);

  const highlightRolesForCell = (cellIndex) =>
    [
      {
        role: 'assigned',
        active: assignedSet.has(cellIndex),
        fill: colors.assignedHighlight,
        outline: colors.assignedOutline,
      },
      {
        role: 'eliminated',
        active: eliminatedSet.has(cellIndex),
        fill: colors.eliminatedHighlight,
        outline: colors.eliminatedOutline,
      },
      {
        role: 'pattern',
        active: patternSet.has(cellIndex),
        fill: colors.patternHighlight,
        outline: colors.patternOutline,
      },
      {
        role: 'overlap',
        active: overlapSet.has(cellIndex),
        fill: colors.overlapHighlight,
        outline: colors.overlapOutline,
      },
    ].filter((highlight) => highlight.active);

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT.toFixed(5)}`}
      role="img"
      aria-label={ariaLabel}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      focusable="false"
    >
      <title>{ariaLabel}</title>
      <rect width={VIEWBOX_WIDTH} height={VIEWBOX_HEIGHT} fill={colors.background} />
      <polygon
        points={polygonPoints(
          GEOMETRY.outerTriangle.map((point) => ({ x: point.x, y: point.y + 4 }))
        )}
        fill={colors.shadow}
        opacity="0.2"
      />
      <polygon points={polygonPoints(GEOMETRY.outerTriangle)} fill={colors.board} />

      {GEOMETRY.cells.map((cell) => (
        <polygon
          key={`fill-${cell.index}`}
          points={polygonPoints(cell.points)}
          fill={cellBaseFill(cell.index, colors)}
          stroke="none"
          data-cell-index={cell.index}
          data-cell-layer="base"
        />
      ))}

      {GEOMETRY.cells.map((cell) => {
        const highlight = highlightRolesForCell(cell.index)[0];
        return highlight ? (
          <polygon
            key={`highlight-${cell.index}`}
            points={polygonPoints(cell.points)}
            fill={highlight.fill}
            fillOpacity="0.72"
            stroke="none"
            data-cell-index={cell.index}
            data-cell-layer="highlight"
          />
        ) : null;
      })}

      {[...GEOMETRY.edges.values()].map((edge) => (
        <line
          key={`minor-${edge.key}`}
          x1={edge.start.x}
          y1={edge.start.y}
          x2={edge.end.x}
          y2={edge.end.y}
          stroke={colors.stroke}
          strokeWidth={MINOR_STROKE_WIDTH}
          strokeLinecap="round"
        />
      ))}

      {[...GEOMETRY.edges.values()]
        .filter((edge) => edge.isMajor)
        .map((edge) => (
          <line
            key={`major-${edge.key}`}
            x1={edge.start.x}
            y1={edge.start.y}
            x2={edge.end.x}
            y2={edge.end.y}
            stroke={colors.stroke}
            strokeWidth={MAJOR_STROKE_WIDTH}
            strokeLinecap="round"
          />
        ))}

      {GEOMETRY.cells.flatMap((cell) =>
        highlightRolesForCell(cell.index)
          .slice(1)
          .map((highlight, index) => (
            <polygon
              key={`secondary-highlight-${cell.index}-${highlight.role}`}
              points={polygonPoints(
                cell.points.map((point) =>
                  offsetPointToward(
                    point,
                    cell.centroid,
                    CELL_SIDE * (0.055 + index * 0.045)
                  )
                )
              )}
              fill="none"
              stroke={highlight.outline}
              strokeWidth="5.2"
              strokeLinejoin="round"
              pointerEvents="none"
              data-cell-index={cell.index}
              data-cell-layer="secondary-highlight"
              data-highlight-role={highlight.role}
            />
          ))
      )}

      {sourcePaths.map((path, index) => (
        <path
          key={`source-unit-${index}`}
          d={path}
          fill="none"
          stroke={colors.sourceOutline}
          strokeWidth="7.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity="0.95"
          data-unit-role="source"
        />
      ))}

      {peerPaths.map((path, index) => (
        <path
          key={`peer-unit-${index}`}
          d={path}
          fill="none"
          stroke={colors.peerOutline}
          strokeWidth="5.4"
          strokeDasharray="10 8"
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity="0.95"
          data-unit-role="peer"
        />
      ))}

      {GEOMETRY.cells.map((cell) => {
        const display = displays.get(cell.index);
        if (!display?.values.length || Number(values[cell.index])) {
          return null;
        }

        return candidatePositions(cell, display.values).map(({ digit, x, y }) => {
          const isEliminated = display.eliminatedValues.has(digit);
          const isRetained = display.retainedValues.has(digit);
          const isCircled = display.circledValues.has(digit);
          const fill = isEliminated
            ? colors.peerOutline
            : isRetained || isCircled
            ? colors.sourceOutline
            : colors.candidateText;

          return (
            <g key={`candidate-${cell.index}-${digit}`}>
              {isCircled ? (
                <circle
                  cx={x}
                  cy={y - CANDIDATE_FONT_SIZE * 0.34}
                  r={CANDIDATE_FONT_SIZE * 0.54}
                  fill="none"
                  stroke={colors.sourceOutline}
                  strokeWidth="2.4"
                />
              ) : null}
              <text
                x={x}
                y={y}
                textAnchor="middle"
                fontFamily={FONT_FAMILY}
                fontSize={CANDIDATE_FONT_SIZE}
                fontWeight={isEliminated || isRetained || isCircled ? '700' : '550'}
                fill={fill}
                opacity="0.97"
                data-candidate-cell={cell.index}
              >
                {digit}
              </text>
              {isEliminated ? (
                <line
                  x1={x - CANDIDATE_FONT_SIZE * 0.31}
                  y1={y + CANDIDATE_FONT_SIZE * 0.08}
                  x2={x + CANDIDATE_FONT_SIZE * 0.31}
                  y2={y - CANDIDATE_FONT_SIZE * 0.72}
                  stroke={colors.peerOutline}
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
              ) : null}
            </g>
          );
        });
      })}

      {GEOMETRY.cells.map((cell) => {
        const value = Number(values[cell.index]);
        if (!Number.isInteger(value) || value < 1 || value > 9) {
          return null;
        }

        const position = legacyValuePosition(cell);
        const isGiven = givenSet.has(cell.index);
        const opticalXOffset = value === 1 ? -0.035 * VALUE_FONT_SIZE : 0;

        return (
          <text
            key={`value-${cell.index}`}
            x={position.x + opticalXOffset}
            y={position.y}
            textAnchor="middle"
            fontFamily={FONT_FAMILY}
            fontSize={VALUE_FONT_SIZE}
            fontWeight="400"
            fill={isGiven ? colors.givenText : colors.answerText}
            data-value-cell={cell.index}
            data-given={isGiven ? 'true' : 'false'}
          >
            {value}
          </text>
        );
      })}
    </svg>
  );
}
